import { useMemo } from "react";

import __ from "web/Layout/Translations";

import { ProductCodeFrontendStatus } from "web/types/Code";

interface ICodeItemStatus {
  switchable?: boolean;
  disabled?: boolean;
  label?: CodeItemStatusLabel;
  isCanceled?: boolean;
  active?: boolean;
  isExpired?: boolean | "";
  statusCode: CodeItemStatusCode;
}

export const enum CodeItemStatusLabel {
  CANCELED = "Anulowany",
  PENDING = "Przygotowujemy Twój kod",
  EXPIRED = "Przeterminowany",
  CAN_BE_USED = "Do wykorzystania",
  IS_USED = "Wykorzystany",
  WAITING_FOR_PAYMENT = "Oczekuje na płatność",
  TO_WITHDRAW = "Zgłoszono odstąpienie",
  WITHDRAWING = "W trakcie zwrotu",
  WITHDRAWN = "Odstąpiono od zakupu",
  WITHDRAW_ERROR = "Błąd zwrotu",
}

export const enum CodeItemStatusCode {
  AVAILABLE = "available",
  USED = "used",
  EXPIRED = "expired",
  CANCELED = "canceled",
  WAITING = "waiting",
  WAITING_FOR_PAYMENT = "waiting_for_payment",
  TO_WITHDRAW = "to_withdraw",
  WITHDRAWING = "withdrawing",
  WITHDRAWN = "withdrawn",
  WITHDRAW_ERROR = "withdrawn_error",
}

export const inactiveStatusCodes = [
  CodeItemStatusCode.CANCELED,
  CodeItemStatusCode.EXPIRED,
  CodeItemStatusCode.USED,
  CodeItemStatusCode.WAITING_FOR_PAYMENT,
  CodeItemStatusCode.WAITING,
  CodeItemStatusCode.TO_WITHDRAW,
  CodeItemStatusCode.WITHDRAWING,
  CodeItemStatusCode.WITHDRAWN,
  CodeItemStatusCode.WITHDRAW_ERROR,
];

export const orderDetailsStatusCodes = [
  CodeItemStatusCode.CANCELED,
  CodeItemStatusCode.EXPIRED,
  CodeItemStatusCode.USED,
  CodeItemStatusCode.TO_WITHDRAW,
  CodeItemStatusCode.WITHDRAWING,
  CodeItemStatusCode.WITHDRAWN,
  CodeItemStatusCode.WITHDRAW_ERROR,
];

const useCodeItemStatus = (
  productCodeFrontendStatus: ProductCodeFrontendStatus
): ICodeItemStatus =>
  useMemo(() => {
    switch (productCodeFrontendStatus) {
      case ProductCodeFrontendStatus.CANCELED:
        return {
          disabled: true,
          label: __(CodeItemStatusLabel.CANCELED) as CodeItemStatusLabel,
          isCanceled: true,
          active: false,
          isExpired: false,
          statusCode: CodeItemStatusCode.CANCELED,
        };

      case ProductCodeFrontendStatus.EXPIRED:
        return {
          disabled: false,
          label: __(CodeItemStatusLabel.EXPIRED) as CodeItemStatusLabel,
          isCanceled: false,
          active: false,
          isExpired: true,
          statusCode: CodeItemStatusCode.EXPIRED,
        };

      case ProductCodeFrontendStatus.USED:
        return {
          switchable: true,
          disabled: false,
          label: __(CodeItemStatusLabel.IS_USED) as CodeItemStatusLabel,
          isCanceled: false,
          active: false,
          isExpired: false,
          statusCode: CodeItemStatusCode.USED,
        };

      case ProductCodeFrontendStatus.TO_WITHDRAW:
        return {
          disabled: true,
          label: __(CodeItemStatusLabel.TO_WITHDRAW) as CodeItemStatusLabel,
          isCanceled: false,
          active: false,
          isExpired: false,
          statusCode: CodeItemStatusCode.TO_WITHDRAW,
        };

      case ProductCodeFrontendStatus.WITHDRAWING:
      case ProductCodeFrontendStatus.WITHDRAWN:
        return {
          disabled: true,
          label: __(CodeItemStatusLabel.WITHDRAWN) as CodeItemStatusLabel,
          isCanceled: false,
          active: false,
          isExpired: false,
          statusCode: CodeItemStatusCode.WITHDRAWN,
        };

      case ProductCodeFrontendStatus.WITHDRAW_ERROR:
        return {
          disabled: true,
          label: __(CodeItemStatusLabel.WITHDRAW_ERROR) as CodeItemStatusLabel,
          isCanceled: false,
          active: false,
          isExpired: false,
          statusCode: CodeItemStatusCode.WITHDRAW_ERROR,
        };

      case ProductCodeFrontendStatus.PAID:
        return {
          switchable: true,
          disabled: false,
          label: __(CodeItemStatusLabel.CAN_BE_USED) as CodeItemStatusLabel,
          isCanceled: false,
          active: true,
          isExpired: false,
          statusCode: CodeItemStatusCode.AVAILABLE,
        };

      case ProductCodeFrontendStatus.PENDING_PAYMENT:
        return {
          disabled: true,
          label: __(
            CodeItemStatusLabel.WAITING_FOR_PAYMENT
          ) as CodeItemStatusLabel,
          isCanceled: false,
          active: true,
          isExpired: false,
          statusCode: CodeItemStatusCode.WAITING_FOR_PAYMENT,
        };

      case ProductCodeFrontendStatus.PENDING_CODE:
      default:
        return {
          disabled: true,
          label: __(CodeItemStatusLabel.PENDING) as CodeItemStatusLabel,
          isCanceled: false,
          active: true,
          isExpired: false,
          statusCode: CodeItemStatusCode.WAITING,
        };
    }
  }, [productCodeFrontendStatus]);

export default useCodeItemStatus;
